import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  GoodsReceiptMutationUpdateProductDetailsArgs,
  GoodsReceiptProductDetail,
  GoodsReceiptProductDetailsBatchUpdateInput,
  GoodsReceiptProductDetailsUpdateFailure,
  GoodsReceiptProductDetailsUpdateSuccess,
  UpdateGoodsReceiptProductDetailsMutation,
} from "@/graphql/codegen/graphql"
import { UpdateGoodsReceiptProductDetailsDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { ActionOutcome } from "@/types/common"

interface UseUpdateGoodsReceiptLinesReturn {
  update: (input: GoodsReceiptProductDetailsBatchUpdateInput) => Promise<ActionOutcome<GoodsReceiptProductDetail[]>>
  isUpdating: boolean
  error?: CombinedError
}

export const useUpdateGoodsReceiptLines = (onSuccess: () => void): UseUpdateGoodsReceiptLinesReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    UpdateGoodsReceiptProductDetailsMutation,
    GoodsReceiptMutationUpdateProductDetailsArgs
  >(UpdateGoodsReceiptProductDetailsDocument)

  const update = async (
    input: GoodsReceiptProductDetailsBatchUpdateInput
  ): Promise<ActionOutcome<GoodsReceiptProductDetail[]>> => {
    const { data, error } = await executeMutation({ input })
    const output = data?.goodsReceipt.updateProductDetails
    const hasError = error || output?.__typename === "GoodsReceiptProductDetailsUpdateFailure"

    if (hasError) {
      const errorMessage =
        error?.message ??
        (output as GoodsReceiptProductDetailsUpdateFailure).error.message ??
        "Failed to update goods receipt lines"
      notify({
        type: NotificationType.Error,
        message: errorMessage,
      })
      return {
        error: new Error(errorMessage),
      }
    }

    notify({
      type: NotificationType.Success,
      content: "Goods receipt lines successfully updated",
    })
    onSuccess()

    return {
      data: (output as GoodsReceiptProductDetailsUpdateSuccess).goodsReceipt.productDetails ?? [],
    }
  }

  return {
    update,
    isUpdating: fetching,
    error: error,
  }
}
